import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { 
  Shield, Users, Key, Building, Search, ArrowRight 
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const SignIn = () => {
  const { isArabic } = useLanguage();

  const portals = [
    {
      id: 'landlord',
      title: isArabic ? 'بوابة الملاك' : 'Landlord Portal',
      description: isArabic 
        ? 'أضف عقاراتك للإيجار أو البيع، تتبع القوائم، واستلم الاستفسارات'
        : 'List properties for rent or sale, track listings, and receive enquiries',
      icon: Key,
      color: 'bg-[#FF8C42]',
      hoverColor: 'hover:border-[#FF8C42]',
      link: '/landlord/login',
      features: [
        isArabic ? 'إضافة وإدارة العقارات' : 'Add & manage properties',
        isArabic ? 'تتبع الاستفسارات والمشاهدات' : 'Track enquiries & viewings',
        isArabic ? 'تقارير الأداء' : 'Performance reports'
      ]
    },
    {
      id: 'architecture',
      title: isArabic ? 'عميل التصميم المعماري' : 'Architecture Client',
      description: isArabic 
        ? 'تتبع مشاريع التصميم، عرض الرسومات، والتواصل مع فريق المهندسين'
        : 'Track design projects, view drawings, and communicate with our architects',
      icon: Building,
      color: 'bg-blue-500',
      hoverColor: 'hover:border-blue-500',
      link: '/client/login',
      features: [
        isArabic ? 'تتبع تقدم المشروع' : 'Track project progress',
        isArabic ? 'عرض وتحميل الرسومات' : 'View & download drawings',
        isArabic ? 'التواصل مع الفريق' : 'Communicate with team'
      ]
    },
    {
      id: 'seeker',
      title: isArabic ? 'الباحث عن عقار' : 'Property Seeker',
      description: isArabic 
        ? 'احفظ العقارات المفضلة، احصل على تنبيهات، وتتبع طلباتك'
        : 'Save favourite properties, get alerts, and track your applications',
      icon: Search,
      color: 'bg-green-500',
      hoverColor: 'hover:border-green-500',
      link: '/seeker/login',
      features: [
        isArabic ? 'حفظ المفضلات' : 'Save favourites',
        isArabic ? 'تنبيهات العقارات الجديدة' : 'New property alerts',
        isArabic ? 'تتبع طلباتك' : 'Track applications'
      ]
    },
    {
      id: 'employee',
      title: isArabic ? 'بوابة الموظفين' : 'Employee Portal',
      description: isArabic 
        ? 'الوصول إلى المهام المسندة، إدارة العملاء، وتتبع الأهداف'
        : 'Access assigned tasks, manage clients, and track your targets',
      icon: Users,
      color: 'bg-purple-500',
      hoverColor: 'hover:border-purple-500',
      link: '/employee/login',
      features: [
        isArabic ? 'عرض المهام' : 'View tasks',
        isArabic ? 'إدارة العملاء' : 'Manage clients',
        isArabic ? 'تتبع الأداء' : 'Track performance'
      ]
    },
    {
      id: 'admin',
      title: isArabic ? 'لوحة التحكم' : 'Admin Dashboard',
      description: isArabic 
        ? 'إدارة كاملة للنظام، الموظفين، العقارات، والتقارير'
        : 'Full system management, staff, properties, and reporting',
      icon: Shield,
      color: 'bg-gray-700',
      hoverColor: 'hover:border-gray-700',
      link: '/admin/login',
      features: [
        isArabic ? 'إدارة النظام' : 'System management',
        isArabic ? 'إدارة الموظفين' : 'Staff management',
        isArabic ? 'التقارير والتحليلات' : 'Reports & analytics'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-24 pb-12" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-[#404040] mb-4">
            {isArabic ? 'تسجيل الدخول' : 'Sign In'}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {isArabic 
              ? 'اختر نوع حسابك للوصول إلى البوابة المخصصة لك'
              : 'Choose your account type to access your dedicated portal'}
          </p>
        </div>

        {/* Portal Cards - Top Row (Main Users) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {portals.slice(0, 3).map((portal) => (
            <Link to={portal.link} key={portal.id} className="group" data-testid={`signin-${portal.id}`}>
              <Card className={`h-full border-2 border-transparent ${portal.hoverColor} transition-all duration-300 shadow-lg hover:shadow-xl`}>
                <CardContent className="p-6">
                  <div className={`w-14 h-14 ${portal.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <portal.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#404040] mb-2 group-hover:text-[#FF8C42] transition-colors">
                    {portal.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    {portal.description}
                  </p>
                  <ul className="space-y-2 mb-4">
                    {portal.features.map((feature, idx) => (
                      <li key={idx} className="text-sm text-gray-500 flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full ${portal.color}`}></span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className={`flex items-center gap-2 text-sm font-medium ${portal.color.replace('bg-', 'text-').replace('-500', '-600').replace('-700', '-600')}`}>
                    {isArabic ? 'تسجيل الدخول' : 'Sign In'}
                    <ArrowRight className={`w-4 h-4 group-hover:translate-x-1 transition-transform ${isArabic ? 'rotate-180' : ''}`} />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Portal Cards - Bottom Row (Staff) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {portals.slice(3).map((portal) => (
            <Link to={portal.link} key={portal.id} className="group" data-testid={`signin-${portal.id}`}>
              <Card className={`h-full border-2 border-transparent ${portal.hoverColor} transition-all duration-300 shadow-lg hover:shadow-xl`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 ${portal.color} rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                      <portal.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-[#404040] mb-1 group-hover:text-[#FF8C42] transition-colors">
                        {portal.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-3">
                        {portal.description}
                      </p>
                      <div className={`flex items-center gap-2 text-sm font-medium ${portal.color.replace('bg-', 'text-').replace('-500', '-600').replace('-700', '-600')}`}>
                        {isArabic ? 'تسجيل الدخول' : 'Sign In'}
                        <ArrowRight className={`w-4 h-4 group-hover:translate-x-1 transition-transform ${isArabic ? 'rotate-180' : ''}`} />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Help Section */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 mb-4">
            {isArabic ? 'ليس لديك حساب؟' : "Don't have an account?"}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/landlord/login" className="text-[#FF8C42] hover:underline font-medium">
              {isArabic ? 'تسجيل كمالك عقار' : 'Register as Landlord'}
            </Link>
            <span className="text-gray-300">|</span>
            <Link to="/client/login" className="text-blue-500 hover:underline font-medium">
              {isArabic ? 'تسجيل كعميل تصميم' : 'Register as Architecture Client'}
            </Link>
            <span className="text-gray-300">|</span>
            <Link to="/contact" className="text-gray-600 hover:underline font-medium">
              {isArabic ? 'اتصل بنا للمساعدة' : 'Contact Us for Help'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
